<script lang="ts" setup>

import { useUserStore } from '@/stores/user/useUserStore';
import { ref, storeToRefs, useAppConfig, useSwitchValue } from '#imports';

type Props = {
    isMobile: boolean;
};

defineProps<Props>();

const isOpenMenu = ref(false);

const { network, user } = storeToRefs(useUserStore());

const {
    'collection': {
        'pagination': { initialPage, initialSize },
        'sort': { initialDirection, initialSorting },
        initialWatchListTab
    }
} = useAppConfig();

const {
    'value': isVisibleChainList,
    'set': setVisibleChainList,
    'toggle': toggleVisibleChainList
} = useSwitchValue(false);

const handleClick = () => {
    isOpenMenu.value = !isOpenMenu.value;
};

const showDrops = import.meta.env.VITE_BUILD_NAME === 'stage' || import.meta.env.VITE_BUILD_NAME === 'development';

</script>

<template>
  <Dropdown :is-open="isOpenMenu"
            dropdown-class="align-self-stretch btn-menu display-flex align-items-center justify-center"
            position="headerFixed"
            @update:is-open="(value:boolean) => isOpenMenu = value"
  >
    <template #toggle>
      <button aria-label="menu"
              class="btn-menu__button align-items-center justify-center text-color-primary"
              type="button"
              @click.stop="handleClick"
      >
        <div v-if="isOpenMenu"
             key="close"
        >
          <IconMain :size="24"
                    icon="close"
          />
        </div>
        <div v-else
             key="menu"
        >
          <IconMain :size="24"
                    icon="menu"
          />
        </div>
      </button>
    </template>
    <template #content>
      <div class="menu-block hamburger-menu display-flex flex-column">
        <nav aria-label="Main navigation"
             class="display-flex flex-column links-block"
        >
          <NuxtLink :to="`/collections?size=${initialSize}&page=${initialPage}&sort=${initialSorting}&direction=${initialDirection}&tab=${initialWatchListTab}`"
                    aria-label="Collections"
                    class="display-flex align-items-center gap-8 link hamburger-menu__link"
          >
            <IconMain :size="24"
                      icon="grid"
            />
            <p class="compact-500-accent">
              Collections
            </p>
          </NuxtLink>

          <NuxtLink v-if="showDrops"
                    :to="`/drops`"
                    aria-label="Drops"
                    class="display-flex align-items-center gap-8 link hamburger-menu__link"
          >
            <IconMain :size="24"
                      icon="falling-star"
            />
            <p class="compact-500-accent">
              Drops
            </p>
          </NuxtLink>
          <NuxtLink v-if="user"
                    aria-label="Catalog"
                    class="display-flex align-items-center gap-8 link hamburger-menu__link"
                    :to="`/profile/${user}`"
          >
            <IconMain :size="25"
                      icon="inventory"
            />

            <p class="compact-500-accent">
              My inventory
            </p>
          </NuxtLink>

          <div class="hamburger-menu__link hamburger__inventory">
            <ThemeSwitcher is-mobile />
          </div>
        </nav>

        <div v-if="user"
             class="chain-toggle">
          <CollapsePanel :dropdown-content-class="isVisibleChainList ? 'pb-8' : ''"
                         :is-open="isVisibleChainList"
          >
            <template #toggle>
              <div :aria-label="isVisibleChainList ? 'hide chain list' : 'show chain list'"
                   class="chain-toggle-row display-flex align-items-center gap-12"
                   role="button"
                   type="button"
                   @click="toggleVisibleChainList"
              >
                <div :class="network ? 'chain-toggle-row__icon--default' : 'chain-toggle-row__icon--switch'"
                     class="chain-toggle-row__icon display-flex align-items-center justify-center"
                >
                  <IconMain :icon="network ? network.toUpperCase() : 'switch'"
                            :size="20"
                  />
                </div>
                <div class="grow">
                  <p>Selected Network</p>
                  <p v-if="network">
                    {{ network.toUpperCase() }}
                  </p>
                </div>
                <IconMain :class="isVisibleChainList ? 'chain-toggle-row__arrow--active' : 'chain-toggle-row__arrow--default'"
                          :size="24"
                          icon="chevron-r-l-d"
                />
              </div>
            </template>

            <template #content>
              <Suspense>
                <template #fallback>
                  <LoaderDots />
                </template>
                <LazyWeb3ChainList />
              </Suspense>
            </template>
          </CollapsePanel>
        </div>
        <div class="mt-16">
          <Suspense>
            <template #fallback>
              <LoaderDots />
            </template>
            <LazyWeb3Wallet />
          </Suspense>
        </div>
      </div>
    </template>
  </Dropdown>
</template>

<style lang="scss" scoped>

%spacing-x {
  @include desktop-1920 {
    padding-right: $spacing-600;
    padding-left: $spacing-600;
  }

  @include tablet-768 {
    padding-right: $spacing-400;
    padding-left: $spacing-400;
  }
}

.hamburger {
  &-menu {
    &__link {
      padding-top: $spacing-400;
      padding-bottom: $spacing-400;

      @extend %spacing-x;
    }
  }

  &__inventory {
    border-bottom: 1px solid var(--border-content);
  }
}

.chain-toggle {
  margin-bottom: 16px;
  border-bottom: 1px solid var(--border-content);

  &-row {
    padding-top: 16px;
    padding-bottom: 16px;

    @extend %spacing-x;

    &__icon {
      width: 36px;
      height: 35px;

      &--switch {
        background-color: var(--bg-brand-mojito-primary);
      }

      &--default {
        background-color: var(--bg-secondary);
      }
    }

    &__arrow {
      &--default {
        transform: rotate(270deg);
      }

      &--active {
        transform: rotate(90deg);
      }
    }
  }
}

.btn-menu {
  display: none;
  border: none;

  &__button {
    width: 52px;
    height: 52px;
    background: transparent;
    cursor: pointer;
  }

  @include mobile-576 {
    display: flex;
  }
}

.menu-block {
  width: 100%;
  height: 100%;
  background: var(--layer-floor-1);
}

.links-block {
  padding-top: 16px;
  border-top: 1px solid rgb(233 234 236 / 20%);
}

.link {
  text-decoration: none;
}

.color-grey {
  color: rgb(255 255 255 / 60%);
}

.mt-16 {
  margin-top: 16px;
}

.slide-fade-enter-active {
  transition: all 0.2s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.6s cubic-bezier(1, 0.8, 0.9, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(5px);
  opacity: 0;
}
</style>
